import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FeatureFlag } from 'components/feature-flag';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Routes } from 'routes';
import { createStyles } from 'utils/createStyle';

import {
    DateRangeSelectorProps,
    NewOrderHistoryDateRangeSelector,
    OrderHistoryDateRangeSelector,
} from './OrderHistoryDateRangeSelector';
import { PurchaseHistory } from './PurchaseHistory';
import { useRootSelector } from 'rootStore';
import { selectIsConstructorBrowseEnabledWeb } from 'common/features/store/duck/ffs';
import { HorizontalPills, HorizontalPillsItem } from './HorizontalPills';
import { useConfigureUrlWithAffiliateToken } from 'common/hooks/useGetAffiliateActivationUrl';
import { FLIPGIVE_ORDERHISTORY_URL } from 'common/env';
import { ActivityIndicator } from 'components/activity-indicator/ActivityIndicator';
import { LocalDiningHistory } from './LocalDiningHistory';
import { colors } from 'styles/settings';
import { OnlineHistoryFAQ } from './OnlineHistoryFAQ';

const styles = createStyles({
    headline: ['text-brand-dark text-lg mb-2'],
    linkContainer: ['border-solid border-grey-3 border-t mb-4'],
    icon: ['inline-block text-brand ml-2.5'],
    subHeader: ['text-brand underline'],
    subHeaderContainer: ['mt-3'],
    body: ['text-grey-2'],
    link: ['cursor-pointer', { textDecoration: 'none' }],
    reportContainer: ['flex flex-col w-full md:w-48'],
    reportTitle: ['text-brand-dark text-3xl mb-5 font-light sm:font-normal'],
    historyContainer: ['flex flex-col md:flex-row'],
    sectionContainer: ['py-2 w-full md:pl-20'],
    subsectionContainer: ['flex flex-col justify-center items-center mb-8 mt-12'],
    subsectionHeaderTitle: ['text-brand-dark text-20px font-semibold'],
    subsectionHeaderLink: [
        'text-brand text-16px font-semibold',
        {
            marginTop: '9px',
            marginBottom: '1px',
            textDecoration: 'none',
            borderBottom: `2px solid ${colors.brand}`,
        },
    ],
});

const OrderHistoryReports = () => {
    return (
        <div>
            <div className={styles.headline}>Reports</div>
            <div className={styles.linkContainer}>
                <Link to={Routes.FamilyOrderDetail} target="_blank" className={styles.link}>
                    <div className={styles.subHeaderContainer}>
                        <span className={styles.subHeader}>Order Detail</span>
                        <FontAwesomeIcon icon={faExternalLink} className={styles.icon} />
                    </div>
                    <span className={styles.body}>
                        All orders within a specified timeframe, includes details for each card.
                    </span>
                </Link>
            </div>
            <div className={styles.linkContainer}>
                <Link to={Routes.FamilyOrderHistory} target="_blank" className={styles.link}>
                    <div className={styles.subHeaderContainer}>
                        <span className={styles.subHeader}>Order History</span>
                        <FontAwesomeIcon icon={faExternalLink} className={styles.icon} />
                    </div>
                    <span className={styles.body}>
                        All orders within a specified timeframe excluding card details.
                    </span>
                </Link>
            </div>
        </div>
    );
};

const OrderHistoryReportContainer = () => {
    return (
        <div className={styles.reportContainer}>
            <p className={styles.reportTitle}>Order History</p>
            <FeatureFlag flagKey="enable_family_reporting">
                <OrderHistoryReports />
            </FeatureFlag>
        </div>
    );
};

enum OrderHistoryTabs {
    GiftCards = 'Gift Cards',
    Online = 'Online',
    LocalDining = 'Local Dining',
}

function getTabs(includeOnlineOrders = false) {
    return [
        { text: OrderHistoryTabs.GiftCards, value: OrderHistoryTabs.GiftCards },
        { text: OrderHistoryTabs.Online, value: OrderHistoryTabs.Online },
        includeOnlineOrders && {
            text: OrderHistoryTabs.LocalDining,
            value: OrderHistoryTabs.LocalDining,
            isNew: true,
        },
    ].filter((item) => item) as HorizontalPillsItem[];
}

export const OrderHistorySection = () => {
    const [startDate, setStartDate] = useState<Date>();
    const [endDate, setEndDate] = useState<Date>();
    const [cloDateRange, setCloDateRange] = useState<{ start: Date; end: Date } | null>(null);
    const isAffiliateEnabled = useRootSelector(selectIsConstructorBrowseEnabledWeb);

    const [tabs, setTabs] = useState<HorizontalPillsItem[]>(getTabs(isAffiliateEnabled));
    useEffect(() => {
        setTabs(getTabs(true));
    }, []);

    const [selectedValue, setValue] = useState((tabs[0] as any).value);
    const onItemChange = (value: string) => {
        setValue(value as OrderHistoryTabs);
    };

    const onChange = useCallback(
        (start?: Date, end?: Date) => {
            setStartDate(start);
            setEndDate(end);

            if (start && end) {
                setCloDateRange({ start, end });
            }
        },
        [setStartDate, setEndDate]
    );

    return (
        <div className={styles.sectionContainer}>
            <div>
                <div>
                    {isAffiliateEnabled ? (
                        <NewOrderHistoryHeader
                            onChange={onChange}
                            tabs={tabs}
                            selectedValue={selectedValue}
                            onTabChange={onItemChange}
                        />
                    ) : (
                        <OrderHistoryDateRangeSelector onChange={onChange} />
                    )}
                </div>
                <div>
                    {OrderHistoryTabs.GiftCards === selectedValue && startDate && endDate && (
                        <PurchaseHistory startDate={startDate} endDate={endDate} />
                    )}
                    {OrderHistoryTabs.Online === selectedValue ? <OnlineOrderHistory /> : null}
                    {OrderHistoryTabs.LocalDining === selectedValue ? (
                        <LocalDiningHistory dateRange={cloDateRange} />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

const OnlineOrderHistory = () => {
    const [modalOpen, setModalOpen] = useState(false);

    const [url, isLoading] = useConfigureUrlWithAffiliateToken(
        '<token>',
        FLIPGIVE_ORDERHISTORY_URL
    );

    if (isLoading) {
        return <ActivityIndicator />;
    }

    if (!url) {
        return <p>Unable to load</p>;
    }

    return (
        <div>
            <div className={styles.subsectionContainer}>
                <h4 className={styles.subsectionHeaderTitle}>Online Order History</h4>
                <button className={styles.subsectionHeaderLink} onClick={() => setModalOpen(true)}>
                    Learn More
                </button>
            </div>

            <iframe
                src={url}
                className="mt-8 bg-contain bg-no-repeat bg-center w-full"
                style={{ height: '750px' }}
            />

            <OnlineHistoryFAQ
                modalOpen={modalOpen}
                setModalOpen={(nextOpenValue) => setModalOpen(nextOpenValue)}
            />
        </div>
    );
};

const OrderTypeStyles = createStyles({
    container: 'text-xs mb-2 sm:mb-0 flex whitespace-nowrap overflow-x-auto no-scrollbar',
    item: 'text-base inline-block border rounded-full ml-1 mr-2 cursor-pointer font-semibold mb-2',
    itemSelected: 'bg-pale-blue-50 o:border-2 border-brand text-brand',
    itemInactive: 'border-grey-light text-brand-dark hover:border-brand hover:text-brand',
    text: 'px-4 py-2 inline-block font-semibold',
    newText: [
        'mr-2 px-3 py-1 mr-1 bg-orange rounded-full text-13px text-white relative font-semibold',
        { bottom: '1px' },
    ],
});

interface NewOrderHistoryHeader extends DateRangeSelectorProps {
    tabs: HorizontalPillsItem[];
    selectedValue: string;
    onTabChange: (_: string) => void;
}

const NewOrderHistoryHeader = ({
    tabs,
    onChange,
    selectedValue,
    onTabChange,
}: NewOrderHistoryHeader) => {
    const showDateSelector =
        selectedValue === tabs[0].value || (tabs.length > 2 && selectedValue === tabs[2].value);
    const hideInstructionsOnDateSelector = tabs.length > 2 && selectedValue === tabs[2].value;

    return (
        <div className="flex flex-col lg:flex-row justify-between md:items-center md:items-baseline">
            <div className="flex flex-col">
                <p className="text-2xl text-brand-dark font-semibold mb-4 ml-1 mt-8">Order Type</p>
                <HorizontalPills
                    style={OrderTypeStyles}
                    items={tabs}
                    value={selectedValue}
                    onChange={onTabChange}
                />
            </div>
            <div className="">
                {showDateSelector ? (
                    <NewOrderHistoryDateRangeSelector
                        onChange={onChange}
                        hideOrderHistoryReportCopy={hideInstructionsOnDateSelector}
                    />
                ) : null}
            </div>
        </div>
    );
};

export const OrderHistory = () => {
    return (
        <div className={styles.historyContainer}>
            <OrderHistoryReportContainer />
            <OrderHistorySection />
        </div>
    );
};
